import { z } from 'zod';
const color = z.string().regex(/^#[0-9a-f]{6}$/i);
export const premiumBriefSchema = z.object({
    title: z.string().min(1).max(90),
    subtitle: z.string().max(100).default(''),
    treatments: z.array(z.string().min(1).max(65)).min(1).max(4),
    price: z.string().min(1).max(18),
    originalPrice: z.string().max(18).optional(),
    conditions: z.string().max(150).default(''),
    address: z.string().min(1).max(180).optional(),
    durationSec: z.number().int().min(18).max(40).default(24),
    orientation: z.enum(['portrait', 'square', 'landscape']).default('portrait'),
    accentColor: color.optional(),
    artDirection: z
        .enum(['graphic-bold', 'editorial-luxe', 'clinical-clean'])
        .default('graphic-bold'),
    footageAssetIds: z.array(z.string().min(1)).min(1).max(8),
    referenceAssetIds: z.array(z.string().min(1)).max(3).default([]),
    brandLogoAssetId: z.string().min(1).optional(),
    collaboratorLogoAssetId: z.string().min(1).optional(),
    musicTrackId: z.string().min(1).default('disco-divas'),
    musicStartSec: z.number().min(0).max(120).default(5),
});
/** Break headlines deliberately, rather than relying on browser wrapping. */
export function headlineLines(text, maxChars = 18) {
    const lines = [];
    let line = '';
    for (const word of text.split(/\s+/)) {
        if (line && `${line} ${word}`.length > maxChars) {
            lines.push(line);
            line = word;
        }
        else
            line = line ? `${line} ${word}` : word;
    }
    if (line)
        lines.push(line);
    return lines.join('\n');
}
export function buildPremiumComposition(brief, brand, media, musicUrl, sourceMessageIds, exactOfferText) {
    const fps = 30;
    const [width, height] = brief.orientation === 'portrait'
        ? [1080, 1920]
        : brief.orientation === 'square'
            ? [1080, 1080]
            : [1920, 1080];
    const scale = width / 1080;
    const direction = brief.artDirection;
    const displayFont = direction === 'editorial-luxe'
        ? 'playfair'
        : direction === 'clinical-clean'
            ? 'inter'
            : 'anton';
    const displayWeight = displayFont === 'anton' ? 400 : 700;
    const displaySize = direction === 'editorial-luxe'
        ? 114
        : direction === 'clinical-clean'
            ? 104
            : 156;
    const displayTracking = displayFont === 'inter' ? -0.025 : 0;
    const cardRadius = direction === 'editorial-luxe'
        ? 12
        : direction === 'clinical-clean'
            ? 24
            : 48;
    const accent = brief.accentColor ?? brand.accent;
    const total = brief.durationSec * fps;
    const offerFrames = Math.round(total * 0.25);
    const closingFrames = Math.round(total / 6);
    const openingFrames = Math.round(total / 6);
    const treatmentFrames = total - openingFrames - offerFrames - closingFrames;
    const scenes = [];
    const text = (id, value, y, size, duration, options = {}) => ({
        id,
        kind: 'text',
        text: value,
        x: 0.09,
        y,
        width: 0.82,
        height: 0.2,
        startFrame: 0,
        durationInFrames: duration,
        fontSize: size * scale,
        fontFamily: 'inter',
        fontWeight: 700,
        letterSpacing: -0.025,
        lineHeight: 1.03,
        align: 'center',
        color: '#ffffff',
        entrance: 'rise',
        ...options,
    });
    const fitText = (layer) => {
        if (layer.kind !== 'text' || !layer.text)
            return layer;
        const lines = layer.text.split('\n');
        const maxSize = Math.min((layer.width * width) /
            (Math.max(...lines.map((line) => line.length)) *
                (layer.fontFamily === 'anton' ? 0.5 : 0.57)), (layer.height * height) / (lines.length * (layer.lineHeight ?? 1.03)));
        return {
            ...layer,
            fontSize: Math.floor(Math.min(layer.fontSize ?? 30, maxSize)),
        };
    };
    const header = (duration) => {
        const layers = [
            {
                id: 'brand-plate',
                kind: 'shape',
                x: 0.09,
                y: 0.09,
                width: 0.82,
                height: 0.105,
                background: '#080808',
                radius: 26,
                startFrame: 0,
                durationInFrames: duration,
            },
        ];
        if (brand.logoUrl)
            layers.push({
                id: 'brand-logo',
                kind: 'image',
                url: brand.logoUrl,
                x: brand.collaboratorLogoUrl ? 0.13 : 0.3,
                y: 0.11,
                width: brand.collaboratorLogoUrl ? 0.22 : 0.4,
                height: 0.065,
                fit: 'contain',
                startFrame: 0,
                durationInFrames: duration,
            });
        else
            layers.push(text('brand-name', brand.name, 0.115, 38, duration, { height: 0.05 }));
        if (brand.collaboratorLogoUrl)
            layers.push(text('collaboration', '×', 0.12, 48, duration, {
                x: 0.45,
                width: 0.1,
                height: 0.05,
                color: accent,
            }), {
                id: 'collaborator-logo',
                kind: 'image',
                url: brand.collaboratorLogoUrl,
                x: 0.55,
                y: 0.098,
                width: 0.34,
                height: 0.09,
                fit: 'contain',
                startFrame: 0,
                durationInFrames: duration,
            });
        return layers;
    };
    let cursor = 0;
    let footageIndex = 0;
    const sourceOffsets = new Map();
    const add = (id, duration, layers, footage, background = '#080808') => {
        const scene = {
            id,
            startFrame: cursor,
            durationInFrames: duration,
            background,
            layers: [],
        };
        if (footage) {
            // Split long scenes into real-source windows; never freeze or fabricate treatment motion.
            let local = 0;
            while (local < duration) {
                const clip = media[footageIndex++ % media.length];
                const sourceFrames = Math.floor(clip.durationSec * fps);
                const trim = (sourceOffsets.get(clip.id) ?? 0) % sourceFrames;
                const length = Math.min(duration - local, sourceFrames - trim, 90);
                if (length < 1)
                    throw new Error('Footage has no usable frames');
                scene.layers.push({
                    id: `${id}-footage-${local}`,
                    kind: 'video',
                    url: clip.url,
                    assetId: clip.id,
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                    fit: 'cover',
                    startFrame: local,
                    durationInFrames: length,
                    trimStartFrames: trim,
                });
                sourceOffsets.set(clip.id, trim + length);
                local += length;
            }
            scene.layers.push({
                id: 'scrim',
                kind: 'shape',
                x: 0,
                y: 0,
                width: 1,
                height: 1,
                background: 'linear-gradient(180deg,rgba(0,0,0,.25),rgba(0,0,0,.78))',
                startFrame: 0,
                durationInFrames: duration,
            });
        }
        scene.layers.push(...header(duration), ...layers);
        scene.layers = scene.layers.map(fitText);
        scenes.push(scene);
        cursor += duration;
    };
    add('opening', openingFrames, [
        text('headline', headlineLines(direction === 'graphic-bold'
            ? brief.title.toUpperCase()
            : brief.title, brief.orientation === 'portrait' ? 14 : 30), 0.4, displaySize, openingFrames, {
            color: accent,
            height: 0.3,
            fontFamily: displayFont,
            fontWeight: displayWeight,
            lineHeight: 1.06,
            letterSpacing: displayTracking,
        }),
        text('subtitle', headlineLines(brief.subtitle.toUpperCase(), 28), 0.72, 26, openingFrames, {
            height: 0.12,
            letterSpacing: 0.06,
            fontWeight: 500,
        }),
    ], true);
    let remaining = treatmentFrames;
    brief.treatments.forEach((treatment, index) => {
        const duration = index === brief.treatments.length - 1
            ? remaining
            : Math.floor(treatmentFrames / brief.treatments.length);
        remaining -= duration;
        add(`treatment-${index}`, duration, [
            text('step', `${String(index + 1).padStart(2, '0')} / ${String(brief.treatments.length).padStart(2, '0')}`, 0.59, 30, duration, { color: accent, letterSpacing: 0.12, height: 0.05, align: 'left' }),
            text('treatment', headlineLines(direction === 'graphic-bold' ? treatment.toUpperCase() : treatment, 20), 0.65, displaySize * 0.84, duration, {
                height: 0.24,
                align: 'left',
                fontFamily: displayFont,
                fontWeight: displayWeight,
                letterSpacing: 0,
                lineHeight: 1.08,
            }),
        ], true);
    });
    add('offer', offerFrames, [
        {
            id: 'card-shadow',
            kind: 'shape',
            x: 0.095,
            y: 0.345,
            width: 0.82,
            height: 0.4,
            background: '#ffffff',
            radius: cardRadius,
            startFrame: 0,
            durationInFrames: offerFrames,
        },
        {
            id: 'offer-card',
            kind: 'shape',
            x: 0.08,
            y: 0.33,
            width: 0.82,
            height: 0.4,
            background: '#080808',
            radius: cardRadius,
            startFrame: 0,
            durationInFrames: offerFrames,
        },
        ...(brief.originalPrice
            ? [
                text('original-price', brief.originalPrice, 0.255, 58, offerFrames, {
                    x: 0.15,
                    width: 0.28,
                    height: 0.05,
                    color: '#080808',
                    strike: true,
                }),
                text('now-label', 'NOW JUST', 0.255, 44, offerFrames, {
                    x: 0.45,
                    width: 0.4,
                    height: 0.05,
                    color: '#080808',
                }),
            ]
            : []),
        text('price', brief.price, 0.385, 380, offerFrames, {
            color: accent,
            height: 0.22,
            entrance: 'scale',
            letterSpacing: -0.055,
        }),
        text('included', 'ALL TREATMENTS INCLUDED', 0.64, 30, offerFrames, {
            height: 0.05,
            letterSpacing: 0.06,
            fontWeight: 500,
        }),
        text('offer-title', headlineLines(direction === 'graphic-bold'
            ? brief.title.toUpperCase()
            : brief.title, brief.orientation === 'portrait' ? 28 : 45), 0.78, 44, offerFrames, {
            color: '#080808',
            height: 0.08,
            fontFamily: displayFont,
            fontWeight: displayWeight,
            letterSpacing: 0.015,
        }),
        text('conditions', headlineLines(brief.conditions, brief.orientation === 'portrait' ? 48 : 72), 0.87, 25, offerFrames, {
            color: '#080808',
            height: 0.08,
            fontWeight: 500,
            letterSpacing: displayTracking,
        }),
    ], false, accent);
    add('closing', closingFrames, [
        text('cta', 'BOOK YOUR\nVISIT.', 0.35, displaySize, closingFrames, {
            color: accent,
            height: 0.3,
            fontFamily: displayFont,
            fontWeight: displayWeight,
            letterSpacing: 0,
            lineHeight: 1.08,
        }),
        text('booking', brand.bookingUrl ?? brand.name, 0.72, 34, closingFrames, {
            height: 0.07,
            letterSpacing: 0,
        }),
        text('address', headlineLines(brand.address ?? '', brief.orientation === 'portrait' ? 38 : 64), 0.81, 26, closingFrames, {
            height: 0.12,
            lineHeight: 1.4,
            fontWeight: 500,
            letterSpacing: displayTracking,
        }),
    ], false);
    return {
        version: 1,
        artDirection: direction,
        fps,
        width,
        height,
        scenes,
        music: {
            url: musicUrl,
            trackId: brief.musicTrackId,
            startSec: brief.musicStartSec,
            volume: 0.65,
            fadeInFrames: 12,
            fadeOutFrames: 30,
        },
        provenance: {
            sourceMessageIds,
            referenceAssetIds: brief.referenceAssetIds,
            exactOfferText,
        },
    };
}
export function validatePremiumComposition(doc) {
    const errors = [];
    if (![doc.width, doc.height, doc.fps].every((value) => Number.isFinite(value) && value > 0) ||
        doc.scenes.length === 0)
        errors.push('Invalid canvas or empty composition');
    const sceneIds = new Set();
    let end = 0;
    for (const scene of doc.scenes) {
        if (sceneIds.has(scene.id))
            errors.push(`${scene.id}: duplicate scene identifier`);
        sceneIds.add(scene.id);
        const layerIds = new Set();
        if (!Number.isInteger(scene.startFrame) ||
            !Number.isInteger(scene.durationInFrames) ||
            scene.startFrame !== end ||
            scene.durationInFrames < 1)
            errors.push(`${scene.id}: invalid scene timing`);
        end = scene.startFrame + scene.durationInFrames;
        for (const layer of scene.layers) {
            if (layerIds.has(layer.id))
                errors.push(`${scene.id}/${layer.id}: duplicate layer identifier`);
            layerIds.add(layer.id);
            if (![layer.x, layer.y, layer.width, layer.height].every(Number.isFinite) ||
                layer.x < 0 ||
                layer.y < 0 ||
                layer.width <= 0 ||
                layer.height <= 0 ||
                layer.x + layer.width > 1.001 ||
                layer.y + layer.height > 1.001)
                errors.push(`${scene.id}/${layer.id}: unsafe placement`);
            if (!Number.isInteger(layer.startFrame) ||
                !Number.isInteger(layer.durationInFrames) ||
                layer.startFrame < 0 ||
                layer.durationInFrames < 1 ||
                layer.startFrame + layer.durationInFrames > scene.durationInFrames)
                errors.push(`${scene.id}/${layer.id}: invalid layer timing`);
            if ((layer.kind === 'video' || layer.kind === 'image') && !layer.url)
                errors.push(`${scene.id}/${layer.id}: missing media`);
            if (layer.kind === 'text' && layer.text) {
                const size = layer.fontSize ?? 30;
                const lines = layer.text.split('\n');
                if (lines.some((line) => line.length * size * (layer.fontFamily === 'anton' ? 0.5 : 0.57) >
                    layer.width * doc.width) ||
                    lines.length * size * (layer.lineHeight ?? 1) >
                        layer.height * doc.height)
                    errors.push(`${scene.id}/${layer.id}: text does not fit; shorten the copy`);
            }
        }
    }
    return errors;
}
export function compilePremiumComposition(composition, videoId, orientation) {
    const errors = validatePremiumComposition(composition);
    if (errors.length)
        throw new Error(errors.join('; '));
    return {
        schemaVersion: 2,
        videoId,
        templateDocId: 'premium-branded-ad',
        fps: composition.fps,
        dimensions: { width: composition.width, height: composition.height },
        durationInFrames: composition.scenes.reduce((sum, scene) => sum + scene.durationInFrames, 0),
        orientation,
        root: { kind: 'leaf', id: 'premium', spine: [], overlays: [] },
        globals: { audio: {} },
        premiumComposition: composition,
    };
}
//# sourceMappingURL=premium-composition.js.map